// src/components/Policy/PolicyAgreement.jsx
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '../../supabase';
import './PolicyAgreement.css';

const PolicyAgreement = () => {
  const navigate = useNavigate();
  const [agreed, setAgreed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
  const [userEmail, setUserEmail] = useState('');
  const [userName, setUserName] = useState('');

  // Check if user already agreed (from session storage)
  useEffect(() => {
    const hasAgreed = sessionStorage.getItem('policyAgreed');
    if (hasAgreed === 'true') {
      navigate('/register');
    }
  }, [navigate]);

  // Check if user scrolled to bottom
  const handleScroll = (e) => {
    const element = e.target;
    const scrollTop = element.scrollTop;
    const scrollHeight = element.scrollHeight - element.clientHeight;
    
    if (scrollTop >= scrollHeight - 50) {
      setHasScrolledToBottom(true);
    } else {
      setHasScrolledToBottom(false);
    }
  };

  // Handle agree button click
  const handleAgree = async () => {
    // Validation
    if (!agreed) {
      alert('Please tick the checkbox to confirm you agree.');
      return;
    }
    if (!hasScrolledToBottom) {
      alert('Please scroll to the bottom of the policy document.');
      return;
    }
    if (!userEmail) {
      alert('Please enter your email address.');
      return;
    }
    if (!userName) {
      alert('Please enter your full name.');
      return;
    }

    setLoading(true);

    try {
      // Save to Supabase
      const { error } = await supabase
        .from('policy_agreements')
        .insert([{
          email: userEmail,
          full_name: userName,
          agreed_at: new Date().toISOString(),
          ip_address: 'N/A', // You can use a service to get real IP
          user_agent: navigator.userAgent,
          status: 'agreed',
          policy_version: '1.0',
          has_scrolled: true,
          scroll_percentage: 100
        }]);

      if (error) {
        console.error('Supabase error:', error);
        // Still proceed even if Supabase fails
      }

      // Save to session storage
      sessionStorage.setItem('policyAgreed', 'true');
      sessionStorage.setItem('policyEmail', userEmail);
      sessionStorage.setItem('policyAgreedAt', new Date().toISOString());

      // Redirect to registration
      navigate('/register');

    } catch (error) {
      console.error('Error:', error);
      alert('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="policy-agreement-page">
      {/* Hero Section */}
      <div className="policy-hero">
        <div className="policy-hero-overlay"></div>
        <div className="policy-hero-content">
          <span className="policy-hero-badge">AICIS 2026</span>
          <h1>Policy Agreement</h1>
          <p>Please read and agree to our terms before registering.</p>
        </div>
      </div>

      {/* Main Content */}
      <div className="policy-container">
        <div className="policy-wrapper">
          
          {/* User Info Section */}
          <div className="policy-user-section">
            <div className="policy-input-group">
              <label>Full Name *</label>
              <input
                type="text"
                placeholder="Enter your full name"
                value={userName}
                onChange={(e) => setUserName(e.target.value)}
                required
              />
            </div>
            <div className="policy-input-group">
              <label>Email Address *</label>
              <input
                type="email"
                placeholder="Enter your email address"
                value={userEmail}
                onChange={(e) => setUserEmail(e.target.value)}
                required
              />
            </div>
          </div>

          {/* Scrollable Policy Document */}
          <div 
            className="policy-scroll-container"
            onScroll={handleScroll}
          >
            <div className="policy-content">
              
              {/* SECTION 1: Cancellation & Refund */}
              <div className="policy-section">
                <h2>1. Cancellation & Refund Policy</h2>
                <p>
                  The Africa Infrastructure, Climate Change, Security & Green Investment Summit (AICIS) 
                  is organized by <strong>Africa Business Ventures & Investment Group (ABVIG)</strong>.
                </p>
                
                <h4>1.1 Cancellation by Participant</h4>
                <ul>
                  <li>Cancellation requests must be submitted in writing at least <strong>30 calendar days</strong> before the Summit.</li>
                  <li>Approved cancellations are eligible for a refund of up to <strong>60%</strong> of the registration fee.</li>
                  <li>Requests received less than 30 days before the Summit are not eligible for a refund.</li>
                </ul>

                <h4>1.2 Non-Refundable Charges</h4>
                <ul>
                  <li>Bank transaction and transfer charges</li>
                  <li>International remittance fees</li>
                  <li>SWIFT/Telex charges</li>
                  <li>Payment gateway processing fees</li>
                  <li>VAT and applicable taxes</li>
                </ul>

                <h4>1.3 Eligible Grounds for Refund</h4>
                <ul>
                  <li>Official visa refusal</li>
                  <li>Government-imposed travel restrictions</li>
                  <li>Serious illness or medical emergency</li>
                  <li>Death of an immediate family member</li>
                </ul>
              </div>

              {/* SECTION 2: Visa & Immigration */}
              <div className="policy-section">
                <h2>2. Visa & Immigration Policy</h2>
                <p>
                  Upon confirmation of registration and payment, participants may receive:
                </p>
                <ul>
                  <li>Official Letter of Invitation</li>
                  <li>Confirmation of Registration</li>
                  <li>Supporting conference documentation</li>
                </ul>
                <p className="policy-warning">
                  <strong>⚠️ These documents do not guarantee visa approval.</strong>
                </p>
                <p>
                  ABVIG and AICIS shall not be liable for:
                </p>
                <ul>
                  <li>Visa refusal or processing delays</li>
                  <li>Immigration decisions</li>
                  <li>Entry refusals</li>
                </ul>
              </div>

              {/* SECTION 3: Conduct & Compliance */}
              <div className="policy-section">
                <h2>3. Participant Conduct Policy</h2>
                <p>
                  Participants agree to:
                </p>
                <ul>
                  <li>Respect the laws of the host country</li>
                  <li>Observe immigration and border control requirements</li>
                  <li>Maintain professional conduct during the Summit</li>
                </ul>
                <div className="policy-warning-box">
                  <strong>⚠️ Warning:</strong> Fraudulent use of registration may result in:
                  <ul>
                    <li>Cancellation without refund</li>
                    <li>Disqualification from future events</li>
                    <li>Reporting to relevant authorities</li>
                  </ul>
                </div>
              </div>

              {/* SECTION 4: Event Modification */}
              <div className="policy-section">
                <h2>4. Event Modification & Force Majeure</h2>
                <p>
                  The Organisers reserve the right to postpone, reschedule, or cancel the Summit due to:
                </p>
                <ul>
                  <li>Natural disasters</li>
                  <li>Public health emergencies</li>
                  <li>Civil unrest or security concerns</li>
                  <li>Government directives</li>
                </ul>
              </div>

              {/* SECTION 5: Privacy */}
              <div className="policy-section">
                <h2>5. Privacy & Data Protection</h2>
                <p>
                  By registering, participants consent to the collection and use of their personal information 
                  for conference administration, communications, and security purposes.
                </p>
              </div>

              {/* SECTION 6: Photography */}
              <div className="policy-section">
                <h2>6. Photography & Media Consent</h2>
                <p>
                  Participants grant ABVIG and AICIS permission to use photographs and recordings for:
                </p>
                <ul>
                  <li>Event reporting</li>
                  <li>Promotional materials</li>
                  <li>Publications</li>
                  <li>Social media</li>
                </ul>
              </div>

              {/* SECTION 7: Acceptance */}
              <div className="policy-section">
                <h2>7. Acceptance of Terms</h2>
                <p>
                  Registration and payment constitute acceptance of these policies.
                </p>
                <p>
                  Participants confirm they have read, understood, and agree to comply with all Summit rules.
                </p>
              </div>

              {/* Scroll Indicator */}
              <div className="policy-scroll-indicator">
                {hasScrolledToBottom ? (
                  <span className="scroll-done">✓ You've read the full policy</span>
                ) : (
                  <span className="scroll-more">↓ Scroll down to continue</span>
                )}
              </div>
            </div>
          </div>

          {/* Footer with Agreement */}
          <div className="policy-footer">
            <div className="policy-checkbox">
              <label>
                <input
                  type="checkbox"
                  checked={agreed}
                  onChange={(e) => setAgreed(e.target.checked)}
                />
                <span>I have read, understood, and agree to the policies above.</span>
              </label>
            </div>

            <div className="policy-actions">
              <button 
                className="policy-agree-btn"
                onClick={handleAgree}
                disabled={!agreed || !hasScrolledToBottom || loading || !userEmail || !userName}
              >
                {loading ? 'Processing...' : 'I Agree & Proceed to Registration'}
              </button>

              <div className="policy-secondary-actions">
                <Link to="/" className="policy-cancel">Cancel</Link>
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default PolicyAgreement;